"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  INPUT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  evaluateForm,
  FORM_IDS,
  FORMS,
  type FormId,
  type FormLine,
  formText,
  isComplete,
  type LineGuide,
  printPoemPage,
} from "@/lib/tool-kit/poetry/forms";
import { POETRY_READING_STRINGS } from "@/lib/tool-kit/poetry/strings";
import { usePoetryDictionary } from "@/lib/tool-kit/poetry/use-dictionary";
import { printHtmlDocument } from "@/lib/tool-kit/print";

const EXAMPLES: Partial<Record<FormId, readonly string[]>> = {
  haiku: ["Old pond", "frogs jumping in", "sound of water"],
  cinquain: [
    "Listen . . .",
    "With faint dry sound,",
    "Like steps of passing ghosts,",
    "The leaves, frost-crisp’d, break from the trees",
    "And fall.",
  ],
  limerick: [
    "There was an Old Man with a beard,",
    "Who said, “It is just as I feared!",
    "Two Owls and a Hen,",
    "Four Larks and a Wren,",
    "Have all built their nests in my beard!”",
  ],
  shakespearean: [
    "Shall I compare thee to a summer’s day?",
    "Thou art more lovely and more temperate:",
    "Rough winds do shake the darling buds of May,",
    "And summer’s lease hath all too short a date:",
    "Sometime too hot the eye of heaven shines,",
    "And often is his gold complexion dimm’d,",
    "And every fair from fair sometime declines,",
    "By chance, or nature’s changing course untrimm’d:",
    "But thy eternal summer shall not fade,",
    "Nor lose possession of that fair thou ow’st,",
    "Nor shall death brag thou wander’st in his shade,",
    "When in eternal lines to time thou grow’st,",
    "So long as men can breathe, or eyes can see,",
    "So long lives this, and this gives life to thee.",
  ],
  petrarchan: [
    "The world is too much with us; late and soon,",
    "Getting and spending, we lay waste our powers:",
    "Little we see in nature that is ours;",
    "We have given our hearts away, a sordid boon!",
    "This Sea that bares her bosom to the moon;",
    "The Winds that will be howling at all hours,",
    "And are up-gathered now like sleeping flowers;",
    "For this, for every thing, we are out of tune;",
    "It moves us not—Great God! I’d rather be",
    "A Pagan suckled in a creed outworn;",
    "So might I, standing on this pleasant lea,",
    "Have glimpses that would make me less forlorn;",
    "Have sight of Proteus coming from the sea;",
    "Or hear old Triton blow his wreathed horn.",
  ],
  villanelle: [
    "They are all gone away,",
    "The House is shut and still,",
    "There is nothing more to say.",
    "Through broken walls and gray",
    "The winds blow bleak and shrill:",
    "",
    "Nor is there one to-day",
    "To speak them good or ill:",
    "",
    "Why is it then we stray",
    "Around that sunken sill?",
    "",
    "And our poor fancy-play",
    "For them is wasted skill:",
    "",
    "There is ruin and decay",
    "In the House on the Hill:",
  ],
};

/** English strings. A translated content file overrides these through `ui`. */
export const POETRY_FORM_TEMPLATE_STRINGS = {
  ...POETRY_READING_STRINGS,
  formLabel: "Form",
  forms: {
    haiku: "Haiku",
    tanka: "Tanka",
    cinquain: "Cinquain (Crapsey)",
    diamante: "Diamante",
    limerick: "Limerick",
    shakespearean: "Shakespearean sonnet",
    petrarchan: "Petrarchan sonnet",
    villanelle: "Villanelle",
  },
  summaries: {
    haiku: "Three short lines, traditionally 5-7-5 syllables in English.",
    tanka: "Five lines of 5-7-5-7-7 syllables, with a turn in the third line.",
    cinquain: "Adelaide Crapsey’s five lines of 2-4-6-8-2 syllables.",
    diamante: "A seven-line diamond that moves from a subject to its opposite.",
    limerick: "Five comic lines rhyming AABBA, in a bouncing rhythm.",
    shakespearean:
      "Fourteen lines of iambic pentameter rhyming ABAB CDCD EFEF GG.",
    petrarchan:
      "Fourteen lines of iambic pentameter: an ABBAABBA octave and a sestet.",
    villanelle:
      "Nineteen lines on two rhymes, with two refrains that keep returning.",
  },
  rules: {
    haiku: [
      "Three lines of 5, 7 and 5 syllables. The count follows the Japanese 17 sound units; English haiku are often shorter.",
      "Traditionally a season word and a cutting pause shape the poem. In English a dash, a line break or a change of grammar can do the cutting.",
      "No rhyme.",
    ],
    tanka: [
      "Five lines of 5, 7, 5, 7 and 7 syllables, 31 in all.",
      "The upper three lines examine an image and the lower two the personal response, with a turn in the third line.",
      "No rhyme.",
    ],
    cinquain: [
      "Adelaide Crapsey’s form, published in Verse (1915): five lines of 2, 4, 6, 8 and 2 syllables, 22 in all.",
      "Other poets use “cinquain” for any five-line stanza, often rhymed ababb, abaab or abccb.",
    ],
    diamante: [
      "Seven lines, shaped like a diamond, first described by Iris Tiedt in 1969.",
      "Line by line: 1, 2, 3, 4, 3, 2, 1 words. The first half describes the subject; the second half turns to its opposite.",
      "Versions differ on the parts of speech, so the template checks word counts and shows the usual parts of speech as guidance. No rhyme.",
    ],
    limerick: [
      "Five lines rhyming AABBA. Lines 1, 2 and 5 have three beats and lines 3 and 4 have two, in an anapestic rhythm.",
      "The template allows 8–9 syllables in the long lines and 5–6 in the short ones.",
    ],
    shakespearean: [
      "Fourteen lines of iambic pentameter: 10 syllables, or 11 with a feminine ending.",
      "Rhymes run ABAB CDCD EFEF GG: three quatrains and a closing couplet.",
      "The turn (volta) often arrives with the couplet or at line 9.",
    ],
    petrarchan: [
      "Fourteen lines of iambic pentameter: 10 syllables, or 11 with a feminine ending.",
      "An octave rhyming ABBAABBA, then a sestet. The template checks CDCDCD; a CDECDE sestet will show mismatches in lines 11 and 14.",
      "The turn (volta) comes at line 9.",
    ],
    villanelle: [
      "Nineteen lines: five tercets and a closing quatrain, on two rhymes (A and B).",
      "Line 1 returns as lines 6, 12 and 18; line 3 returns as lines 9, 15 and 19. The template fills the refrains in for you.",
      "Rhyme pattern: ABA ABA ABA ABA ABA ABAA. Line length is up to you.",
    ],
  },
  hints: {
    diamante1: "One noun: your subject.",
    diamante2: "Two adjectives describing it.",
    diamante3: "Three -ing or -ed words about it.",
    diamante4: "Four nouns: two for the subject, two for its opposite.",
    diamante5: "Three -ing or -ed words about the opposite.",
    diamante6: "Two adjectives describing the opposite.",
    diamante7: "One noun: the opposite.",
    volta: "The turn (volta): the argument shifts here.",
    couplet: "The closing couplet answers or resolves the quatrains.",
    pivot: "The pivot: the poem turns here from image to feeling.",
    refrainOne: "Refrain A1: this line returns as lines 6, 12 and 18.",
    refrainTwo: "Refrain A2: this line returns as lines 9, 15 and 19.",
  },
  rulesHeading: "The rules",
  linesHeading: "Your lines",
  linePlaceholder: "Line {number}",
  lineLabel: "Line {number}",
  refrainOf: "Refrain: a copy of line {number}",
  rhymeBadge: "Rhyme {letter}",
  syllableTarget: "{count} (aim for {target})",
  syllableOne: "one:{count} syllable|other:{count} syllables",
  targetRange: "{min}–{max}",
  statusMatch: "on target",
  statusOver: "{amount} too many",
  statusUnder: "{amount} short",
  statusPossible:
    "could fit if a word here is read another way (fire, every, blessed…)",
  wordCount: "{count} (aim for {target})",
  wordOne: "one:{count} word|other:{count} words",
  rhymeFirst: "sets the {letter} rhyme",
  rhymeMatch: "rhymes with line {number}",
  rhymeSlant: "slant rhyme with line {number} ({kind})",
  rhymeMismatch: "does not rhyme with line {number}",
  rhymePending: "waiting for line {number}",
  kinds: {
    assonance: "assonance",
    consonance: "consonance",
    "final-vowel": "final vowel",
  },
  complete: "Every line meets the form. Check the sense and sound by ear, too.",
  incomplete:
    "Some lines still need work. Counts use dictionary pronunciations, so a word poets say two ways may read one syllable off.",
  titleLabel: "Title (used for printing and the download)",
  untitled: "Untitled",
  loadExample: "Load a public-domain example",
  exampleCredits: {
    haiku:
      "Bashō’s “old pond” in Lafcadio Hearn’s 1898 translation — a reminder that translated haiku rarely count 5-7-5.",
    cinquain: "Adelaide Crapsey, “November Night”, from Verse (1915).",
    limerick: "Edward Lear, A Book of Nonsense (1846).",
    shakespearean:
      "William Shakespeare, Sonnet 18 (1609), in modernised spelling and punctuation.",
    petrarchan:
      "William Wordsworth, “The world is too much with us” (1807). Its fourth line runs long, as the counts show.",
    villanelle: "Edwin Arlington Robinson, “The House on the Hill” (1894).",
    tanka: "",
    diamante: "",
  },
  clear: "Clear lines",
  copyPoem: "Copy poem",
  copied: "Copied!",
  download: "Download .txt",
  print: "Print",
  filename: "poem.txt",
  honest:
    "Honest templates, no AI: the guides count and compare; every word is yours.",
} as const;

type Strings = typeof POETRY_FORM_TEMPLATE_STRINGS;

const ID = "form";

function syllableText(
  guide: LineGuide,
  spec: FormLine,
  strings: Strings,
  locale: string,
  fmt: (n: number) => string
): string | null {
  if (!spec.syllables || guide.syllableStatus === "empty") {
    return null;
  }
  const [min, max] = spec.syllables;
  const target =
    min === max
      ? fmt(min)
      : t(strings.targetRange, { min: fmt(min), max: fmt(max) });
  const count = tPlural(strings.syllableOne, locale, guide.syllables ?? 0, {
    count: fmt(guide.syllables ?? 0),
  });
  const status = statusFor(guide, strings, locale, fmt);
  return `${t(strings.syllableTarget, { count, target })} — ${status}`;
}

function statusFor(
  guide: LineGuide,
  strings: Strings,
  locale: string,
  fmt: (n: number) => string
): string {
  const amount = (n: number) =>
    tPlural(strings.syllableOne, locale, n, { count: fmt(n) });
  switch (guide.syllableStatus) {
    case "match":
      return strings.statusMatch;
    case "over":
      return t(strings.statusOver, { amount: amount(guide.difference) });
    case "under":
      return t(strings.statusUnder, { amount: amount(-guide.difference) });
    default:
      return strings.statusPossible;
  }
}

function wordText(
  guide: LineGuide,
  spec: FormLine,
  strings: Strings,
  locale: string,
  fmt: (n: number) => string
): string | null {
  if (spec.words === null || guide.wordStatus === "empty") {
    return null;
  }
  const count = tPlural(strings.wordOne, locale, guide.words, {
    count: fmt(guide.words),
  });
  return t(strings.wordCount, { count, target: fmt(spec.words) });
}

function rhymeText(
  guide: LineGuide,
  strings: Strings,
  fmt: (n: number) => string
): string | null {
  const rhyme = guide.rhyme;
  if (!rhyme) {
    return null;
  }
  const number = fmt((rhyme.with ?? 0) + 1);
  switch (rhyme.status) {
    case "first":
      return t(strings.rhymeFirst, { letter: rhyme.letter });
    case "match":
      return t(strings.rhymeMatch, { number });
    case "slant":
      return t(strings.rhymeSlant, {
        number,
        kind: rhyme.kind ? strings.kinds[rhyme.kind] : "",
      });
    case "mismatch":
      return t(strings.rhymeMismatch, { number });
    default:
      return null;
  }
}

export function LineRow({
  index,
  spec,
  guide,
  value,
  onChange,
  strings,
}: {
  index: number;
  spec: FormLine;
  guide: LineGuide;
  value: string;
  onChange: (value: string) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const refrain = spec.refrain !== null;
  const notes = [
    syllableText(guide, spec, strings, locale, fmt),
    wordText(guide, spec, strings, locale, fmt),
    rhymeText(guide, strings, fmt),
  ].filter((note): note is string => note !== null);
  const bad =
    guide.rhyme?.status === "mismatch" ||
    guide.syllableStatus === "over" ||
    guide.syllableStatus === "under";
  const hint = spec.hint
    ? strings.hints[spec.hint as keyof typeof strings.hints]
    : null;
  const fieldId = `${ID}-line-${index}`;
  return (
    <li className="space-y-1">
      <div className="flex items-center gap-3">
        <label
          className="w-14 shrink-0 text-right text-muted-foreground text-xs"
          htmlFor={fieldId}
        >
          {t(strings.lineLabel, { number: fmt(index + 1) })}
          {spec.rhyme ? (
            <span className="block font-semibold text-primary">
              {t(strings.rhymeBadge, { letter: spec.rhyme })}
            </span>
          ) : null}
        </label>
        <input
          className={refrain ? `${INPUT} opacity-70` : INPUT}
          id={fieldId}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t(strings.linePlaceholder, { number: fmt(index + 1) })}
          readOnly={refrain}
          type="text"
          value={refrain ? guide.text : value}
        />
      </div>
      <div className="space-y-0.5 pl-[68px]">
        {refrain ? (
          <p className={HINT}>
            {t(strings.refrainOf, { number: fmt((spec.refrain ?? 0) + 1) })}
          </p>
        ) : null}
        {hint ? <p className={HINT}>{hint}</p> : null}
        {notes.length > 0 ? (
          <p
            className={
              bad ? "text-destructive text-xs" : "text-muted-foreground text-xs"
            }
          >
            {notes.join(" · ")}
          </p>
        ) : null}
      </div>
    </li>
  );
}

export default function PoetryFormTemplateWidget() {
  const strings = useToolUi(POETRY_FORM_TEMPLATE_STRINGS);
  const [formId, setFormId] = useState<FormId>("shakespearean");
  const [texts, setTexts] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const spec = FORMS[formId];
  const hasText = texts.some((text) => text.trim().length > 0);
  const { lookup, status } = usePoetryDictionary(hasText);
  const guides = useMemo(
    () => evaluateForm(spec, texts, lookup),
    [spec, texts, lookup]
  );
  const poem = formText(spec, texts);
  const example = EXAMPLES[formId];
  const finished = hasText && isComplete(guides);
  const credit = strings.exampleCredits[formId];

  function setLine(index: number, value: string) {
    setTexts((previous) =>
      spec.lines.map((_, i) => (i === index ? value : (previous[i] ?? "")))
    );
  }

  function changeForm(next: FormId) {
    setFormId(next);
    setTexts([]);
  }

  const pageTitle = title.trim() || strings.untitled;

  return (
    <>
      <SelectField
        id={`${ID}-select`}
        label={strings.formLabel}
        onChange={changeForm}
        options={FORM_IDS.map((id) => ({
          value: id,
          label: strings.forms[id],
        }))}
        value={formId}
      />
      <div className={`${CARD} space-y-2`}>
        <p className="font-medium text-foreground text-sm">
          {strings.summaries[formId]}
        </p>
        <h3 className="sr-only">{strings.rulesHeading}</h3>
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
          {strings.rules[formId].map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </div>
      <p className={HINT}>{strings.honest}</p>

      {status === "loading" ? (
        <Notice>{strings.dictionaryLoading}</Notice>
      ) : null}
      {status === "failed" ? (
        <ErrorNotice>{strings.dictionaryFailed}</ErrorNotice>
      ) : null}

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.linesHeading}</h2>
        <ol aria-live="polite" className="space-y-4">
          {spec.lines.map((line, index) => (
            <LineRow
              guide={guides[index]}
              index={index}
              key={`${formId}-${index + 1}`}
              onChange={(value) => setLine(index, value)}
              spec={line}
              strings={strings}
              value={texts[index] ?? ""}
            />
          ))}
        </ol>
        {hasText ? (
          <Notice>{finished ? strings.complete : strings.incomplete}</Notice>
        ) : null}
      </section>

      <div className="flex flex-wrap gap-3">
        {example ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setTexts([...example])}
            type="button"
          >
            {strings.loadExample}
          </button>
        ) : null}
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => setTexts([])}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {example && texts.join("|") === example.join("|") && credit ? (
        <p className={HINT}>{credit}</p>
      ) : null}

      {hasText ? (
        <section className="space-y-3">
          <TextField
            id={`${ID}-title`}
            label={strings.titleLabel}
            onChange={setTitle}
            value={title}
          />
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyPoem}
              text={poem}
            />
            <button
              className={BUTTON_SECONDARY}
              onClick={() => downloadText(strings.filename, `${poem}\n`)}
              type="button"
            >
              {strings.download}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => printHtmlDocument(printPoemPage(pageTitle, poem))}
              type="button"
            >
              {strings.print}
            </button>
          </div>
        </section>
      ) : null}
    </>
  );
}
